import { useState, useCallback, useRef } from 'react'

export interface ColumnDef {
  key: string
  label: string
  defaultWidth: number
  minWidth?: number
  maxWidth?: number
  resizable?: boolean
}

interface UseResizableColumnsOptions {
  columns: ColumnDef[]
  storageKey?: string
}

export function useResizableColumns({
  columns,
  storageKey,
}: UseResizableColumnsOptions) {
  const getDefault = useCallback(() => {
    const def: Record<string, number> = {}
    columns.forEach(c => { def[c.key] = c.defaultWidth })
    return def
  }, [columns])

  const [widths, setWidths] = useState<Record<string, number>>(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey)
        if (saved) {
          const parsed = JSON.parse(saved)
          const def = getDefault()
          const merged: Record<string, number> = {}
          columns.forEach(c => {
            merged[c.key] = typeof parsed[c.key] === 'number' ? parsed[c.key] : def[c.key]
          })
          return merged
        }
      } catch {}
    }
    return getDefault()
  })

  // Ref-based resize session — evita problemas de timing con useEffect
  const sessionRef = useRef<{ key: string; startX: number; startWidth: number; min: number; max: number } | null>(null)
  const [, setDragging] = useState(false)

  const handleResizeStart = useCallback((column: ColumnDef, e: React.MouseEvent) => {
    if (column.resizable === false) return
    e.preventDefault()
    e.stopPropagation()

    sessionRef.current = {
      key: column.key,
      startX: e.clientX,
      startWidth: widths[column.key] ?? column.defaultWidth,
      min: column.minWidth ?? 50,
      max: column.maxWidth ?? 700,
    }
    setDragging(true)

    const onMove = (ev: MouseEvent) => {
      const s = sessionRef.current
      if (!s) return
      const diff = ev.clientX - s.startX
      const newWidth = Math.min(s.max, Math.max(s.min, s.startWidth + diff))
      setWidths(prev => {
        const next = { ...prev, [s.key]: newWidth }
        if (storageKey) {
          try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch {}
        }
        return next
      })
    }

    const onUp = () => {
      sessionRef.current = null
      setDragging(false)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [widths, storageKey])

  const resetWidths = useCallback(() => {
    setWidths(getDefault())
    if (storageKey) {
      try { localStorage.removeItem(storageKey) } catch {}
    }
  }, [getDefault, storageKey])

  return {
    widths,
    handleResizeStart,
    resetWidths,
  }
}
